import React, { useEffect, useRef } from "react";
import "./App.css";
import { observer } from "mobx-react";
import { getAuth } from "firebase/auth";
import uiStore from "./MobxStore";
import { getCurrentFirebaseConfig, GoogleSSO } from "./FirebaseApp";
import { FirebaseAppProvider, useFirebaseApp, FirestoreProvider, useFirestore, useFirestoreCollectionData } from "reactfire";
import { collection, CollectionReference, doc, getFirestore, setDoc } from "firebase/firestore";

import UploadImageButton from "./utils/UploadImageButton";
import { logDebug, logError } from "./LocalLogger";
import TopMostPortal from "./Portals";
import { ProductsCreation } from "./Pages/ProductsCreationPage";
import { appStore, webAPIRequest } from "./utils/AppMobx";
import { ConceptsPage } from "./Pages/ConceptsPage";
import printifyService from "./utils/PrintifyService";
import LoadingAnimation from "./UI/LoadingAnimation";
import { SettingsPage } from "./Pages/SettingsPage";
import { RestAPIProvider } from "./utils/RestAPI";
import { PortalPage } from "./Pages/PortalPage";

export interface UsedBlueprint {
	active: boolean;
	// id: number;
	blueprintId: number;
	providerId: string;
	title: string;
}

const UKPoviders: React.FC = observer(() => {
	const [selectedProvider, setSelectedProvider] = React.useState<string | null>(null);
	const [previewImage, setPreviewImage] = React.useState<string | null>(null);
	const firestore = useFirestore();
	const useInAppBlueprintsRef = collection(firestore, "/store/site/useInAppBlueprints") as CollectionReference<UsedBlueprint>;
	const useInAppBlueprintsCD = useFirestoreCollectionData<UsedBlueprint>(useInAppBlueprintsRef, { idField: "useId" });
	if (useInAppBlueprintsCD.status === "loading") {
		return <div>Loading blueprints...</div>;
	}

	return (
		<div className="">
			<div className="flex">
				<h1>Print Providers</h1>
				{appStore.onlyUKProviders.map((provider) => (
					<div
						className="m-2 cursor-pointer hover:bg-gray-200 p-2 rounded whitespace-nowrap"
						key={provider.id}
						onClick={() => {
							setSelectedProvider(provider.id.toString());
							logDebug("Selected provider:", provider.title);
						}}
						style={{
							backgroundColor: selectedProvider === provider.id.toString() ? "#e0f7fa" : "transparent",
						}}
					>
						{provider.title} - {provider.id}
					</div>
				))}
			</div>
			{selectedProvider && (
				<div className="mt-4">
					<h2>Selected Provider: {selectedProvider}</h2>
					{appStore.onlyUKProviders
						.filter((provider) => provider.id.toString() === selectedProvider)
						.map((provider) => (
							<div key={provider.id}>
								<p>
									Location: {provider.location.city}, {provider.location.country}
								</p>
								<p>
									Address: {provider.location.address1}, {provider.location.region}, {provider.location.zip}
								</p>
								<h3>Blueprints:</h3>
								<ul>
									{provider.blueprints?.map((blueprint) => (
										<li key={blueprint.id}>
											{blueprint.title} ({blueprint.brand}) --{" "}
											<input
												checked={useInAppBlueprintsCD.data.some(
													(b) =>
														b.blueprintId === blueprint.id &&
														b.active &&
														b.providerId === provider.id.toString()
												)}
												onChange={async (e) => {
													const isChecked2 = useInAppBlueprintsCD.data.some(
														(b) =>
															b.blueprintId === blueprint.id &&
															b.active &&
															b.providerId === provider.id.toString()
													);
													logDebug(isChecked2, useInAppBlueprintsCD.data, blueprint);
													// return;
													const isChecked = e.target.checked;
													const data = {
														active: isChecked,
														blueprintId: blueprint.id,
														title: blueprint.title,
														providerId: provider.id.toString(),
													};
													const blueprintRef = doc(
														useInAppBlueprintsRef,
														`${provider.id}-${blueprint.id}-${blueprint.title}`
													);
													await setDoc(
														blueprintRef,
														{
															...data,
														},
														{ merge: true }
													);
												}}
												type="checkbox"
											/>{" "}
											Use this blueprint
											<div className="flex gap-2 mt-2">
												{blueprint.images.map((image, index) => (
													<img
														key={index}
														onClick={() => {
															setPreviewImage(image);
														}}
														src={image}
														alt={blueprint.title}
														className="w-16 h-16 object-cover cursor-pointer hover:opacity-80"
													/>
												))}
											</div>
										</li>
									))}
								</ul>
							</div>
						))}
				</div>
			)}
			{previewImage && (
				<TopMostPortal>
					<div className="mt-4 relative">
						<div
							className="absolute top-0 right-0 bg-gray-500 text-white p-2 cursor-pointer"
							onClick={() => setPreviewImage(null)}
							style={{ zIndex: 1000 }}
						>
							X
						</div>
						<img src={previewImage} alt="Preview" width={512} height={512} className="w-128 h-128 object-cover" />
					</div>
				</TopMostPortal>
			)}
		</div>
	);
});

const tabs = ["concepts", "home", "blueprints", "test", "portal", "settings"];
const HomeWithUser: React.FC = observer(() => {
	const [currentTab, setCurrentTab] = React.useState(location.href.includes("localhost") ? "portal" : "concepts");
	return (
		<div>
			<div className="flex gap-4 mb-4">
				{tabs.map((tab) => (
					<button
						key={tab}
						className={`p-2 rounded ${currentTab === tab ? "bg-blue-500 text-white" : "bg-gray-200"}`}
						onClick={() => setCurrentTab(tab)}
					>
						{tab}
					</button>
				))}
			</div>
			{currentTab === "portal" && <PortalPage />}
			{currentTab === "test" && (
				<div>
					<h1>Welcome, {uiStore.user?.displayName || "User"}!</h1>
					<p>Your email: {uiStore.user?.email}</p>
					<UploadImageButton
						onImageSelected={async (fileName, base64Content) => {
							try {
								logDebug("Uploading image:", fileName);
								const response = await printifyService.uploadImage(fileName, base64Content);
								logDebug("Image uploaded successfully:", response);
							} catch (error) {
								logError("Error uploading image:", error);
							}
						}}
						buttonText="Upload Image to Printify"
					/>
				</div>
			)}{" "}
			{currentTab === "blueprints" && <UKPoviders />}
			{currentTab === "settings" && (
				<div>
					<SettingsPage />
				</div>
			)}
			{currentTab === "home" && <ProductsCreation />}
			{currentTab === "concepts" && <ConceptsPage />}
			{appStore.showLoading && <LoadingAnimation fullScreen={true} width={200} height={200} />}
		</div>
	);
});
const Home: React.FC = observer(() => {
	useEffect(() => {
		const auth = getAuth();
		auth.onAuthStateChanged(async (user) => {
			if (user) {
				uiStore.user = user;
				console.debug(`User logged in: ${user.displayName}`);
			} else {
				uiStore.user = null;
				console.debug("User logged out");
			}
		});
	}, []);

	if (uiStore.user === undefined) {
		return <div>Loading...</div>;
	} else if (uiStore.user) {
		const allowedEmails = ["yakir22@gmail.com", "itaice@gmail.com"];
		if (!allowedEmails.includes(uiStore.user.email || "")) {
			return <div>Access denied</div>;
		}
	}
	if (!uiStore.user) {
		return <GoogleSSO />;
	}
	return <HomeWithUser />;
});

function App() {
	const firestore = useRef(getFirestore(useFirebaseApp()));
	const firestoreInstance = firestore.current;
	return (
		<FirestoreProvider sdk={firestoreInstance}>
			<Home />
		</FirestoreProvider>
	);
}

const FirebaseApp: React.FC = () => {
	return (
		<FirebaseAppProvider firebaseConfig={getCurrentFirebaseConfig()}>
			<App />
		</FirebaseAppProvider>
	);
};

export default FirebaseApp;
